import * as React from 'react';
import type { DialogOpenChangeData, DialogOpenChangeEvent } from '@fluentui/react-components';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  Text,
  tokens,
} from '@fluentui/react-components';
import { defineMessage, useIntl } from 'react-intl';

type Props = {
  contactEmail?: string | null;
  onGoToAzurePortal: () => void;
  onContactSupport: () => void;
};

const useStyles = makeStyles({
  pageOverlay: {
    position: 'fixed',
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: tokens.colorBackgroundOverlay,
    zIndex: 1000,
  },
  surface: {
    maxWidth: '600px',
    width: '100%',
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow64,
  },
  body: {
    rowGap: tokens.spacingVerticalS,
  },
  title: {
    marginBottom: tokens.spacingVerticalNone,
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalSNudge,
  },
  bodyText: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  actions: {
    columnGap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
  },
  actionButton: {
    minWidth: '96px',
    whiteSpace: 'nowrap',
  },
});

export function NoAzureActivationModal({ contactEmail, onGoToAzurePortal, onContactSupport }: Props) {
  const intl = useIntl();
  const classes = useStyles();
  const [overlayTopPx, setOverlayTopPx] = React.useState(0);

  React.useEffect(() => {
    function updateOverlayTop() {
      const topChromeSelectors = [
        '[data-testid="assume-user-banner"]',
        '[data-testid="demo-user-banner"]',
        'header[role="banner"]',
      ];

      const topBoundary = topChromeSelectors
        .map((selector) => document.querySelector(selector))
        .filter((element): element is Element => element !== null)
        .reduce((maxBottom, element) => {
          const { bottom } = element.getBoundingClientRect();
          return Math.max(maxBottom, bottom);
        }, 0);

      setOverlayTopPx(Math.max(0, Math.round(topBoundary)));
    }

    updateOverlayTop();
    window.addEventListener('scroll', updateOverlayTop, { passive: true });
    window.addEventListener('resize', updateOverlayTop);

    return () => {
      window.removeEventListener('scroll', updateOverlayTop);
      window.removeEventListener('resize', updateOverlayTop);
    };
  }, []);

  function preventDialogClose(event: DialogOpenChangeEvent, data: DialogOpenChangeData) {
    if (!data.open) {
      event.preventDefault();
    }
  }

  return (
    <>
      <div className={classes.pageOverlay} style={{ top: `${overlayTopPx}px` }} aria-hidden="true" />
      <Dialog open modalType="non-modal" onOpenChange={preventDialogClose}>
        <DialogSurface className={classes.surface}>
          <DialogBody className={classes.body}>
            <DialogTitle className={classes.title} action={null}>
              {intl.formatMessage(title)}
            </DialogTitle>
            <DialogContent className={classes.content}>
              <Text className={classes.bodyText}>{intl.formatMessage(intro)}</Text>
              <Text className={classes.bodyText}>
                {contactEmail
                  ? intl.formatMessage(expiredActivationMessageWithContact, { contactEmail })
                  : intl.formatMessage(expiredActivationMessage)}
              </Text>
            </DialogContent>
            <DialogActions className={classes.actions}>
              <Button className={classes.actionButton} appearance="primary" onClick={onGoToAzurePortal}>
                {intl.formatMessage(goToAzurePortal)}
              </Button>
              <Button className={classes.actionButton} appearance="secondary" onClick={onContactSupport}>
                {intl.formatMessage(contactSupport)}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
}

const title = defineMessage({
  defaultMessage: 'Activation period for your Azure credits expired',
  description: 'No Azure activation modal title',
});

const intro = defineMessage({
  defaultMessage: 'Your credit activation period expired and a new Microsoft for Startups application is required.',
  description: 'No Azure activation modal intro message',
});

const expiredActivationMessage = defineMessage({
  defaultMessage:
    "An email has been sent to your account administrator with instructions on how to reapply in Azure Portal, where you'll activate and manage your credits.",
  description: 'No Azure activation modal body message without contact email',
});

const expiredActivationMessageWithContact = defineMessage({
  defaultMessage:
    "An email has been sent to your account administrator ({contactEmail}) with instructions on how to reapply in Azure Portal, where you'll activate and manage your credits.",
  description: 'No Azure activation modal body message with contact email',
});

const goToAzurePortal = defineMessage({
  defaultMessage: 'Go to Azure Portal',
  description: 'No Azure activation modal primary CTA',
});

const contactSupport = defineMessage({
  defaultMessage: 'Contact support',
  description: 'No Azure activation modal support CTA',
});
